import React, { useEffect, useRef, useState } from "react";
import {
  User,
  Bot,
  Sparkles,
  Brain,
  Clock,
  Building,
  Package2,
  ClipboardList,
  SquareCheckBig,
  ChevronDown,
  ChevronUp,
  Loader2,
} from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import ChatInput from "./ChatInput";
import { ScrollArea } from "../../components/UI/ScrollArea";
import { useSearchParams } from "react-router";
import { base64UrlToUuid } from "../../helpers/shared";

const stepsMapping = {
  crew_kickoff_started: {
    title: "Connecting to Agent",
    description: "Initiating communication...",
    icon: <Building className="h-4 w-4 text-blue-600" />,
  },
  task_started: {
    title: "Collecting information",
    description: "Researching on topic...",
    icon: <Package2 className="h-4 w-4 text-orange-500" />,
  },
  agent_execution_started: {
    title: "Processing",
    description: "Analyzing data...",
    icon: <ClipboardList className="h-4 w-4 text-purple-500" />,
  },
  llm_call_started: {
    title: "Thinking",
    description: "Generating response...",
    icon: <Brain className="h-4 w-4 text-pink-500" />,
  },
  final_response: {
    title: "Finalizing",
    description: "Preparing final answer...",
    icon: <SquareCheckBig className="h-4 w-4 text-green-600" />,
  },
};

const ChatInputLoader = () => {
  return (
    <div className="flex flex-col items-center justify-center py-2">
      <div className="flex space-x-1">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="h-2 w-2 rounded-full bg-gray-400"
            style={{
              animation: `bounce 1s infinite ${i * 0.2}s`,
            }}
          />
        ))}
      </div>
      <style>
        {`
                    @keyframes bounce {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-5px); }
                    }
                `}
      </style>
    </div>
  );
};

const StepItem = ({ step }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="flex flex-col gap-2 py-2 animate-in fade-in slide-in-from-left-2">
      <div className="flex items-start gap-3">
        <div
          className={`mt-0.5 p-1.5 rounded-full bg-white shadow-sm border border-gray-100`}
        >
          {step.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900">{step.title}</p>
            {step.status === "in_progress" && (
              <Loader2 className="h-3 w-3 animate-spin text-blue-500" />
            )}
            {step.status === "completed" && (
              <SquareCheckBig className="h-3 w-3 text-green-500" />
            )}
          </div>
          <p className="text-xs text-gray-500 truncate">{step.description}</p>
        </div>
        {step.details && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </button>
        )}
      </div>
      {isExpanded && step.details && (
        <div className="max-h-90 overflow-y-auto ml-10 p-3 bg-gray-50 rounded-lg border border-gray-100 text-xs text-gray-600 whitespace-pre-wrap font-mono">
          {step.details}
        </div>
      )}
    </div>
  );
};

const ChatPanel = ({
  onSendMessage,
  incomingMessage,
  selectedFile,
  setSelectedFile,
  agentDetails,
  agentLists,
  agentsLoading
}) => {
  const [searchParams] = useSearchParams();
  const agentId = base64UrlToUuid(searchParams.get("agentId")) ?? undefined;
  const agentName = agentDetails?.agent_config?.name || "";
  const [messages, setMessages] = useState([]);
  const [currentSteps, setCurrentSteps] = useState([]);
  const [isStepsExpanded, setIsStepsExpanded] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const initialInput = "";

  const messagesEndRef = useRef(null);
  //const bufferRef = useRef<string[]>([]);
  //const streamTimerRef = useRef<number | null>(null);
  const stepsRef = useRef([]);

  // Sync ref
  useEffect(() => {
    stepsRef.current = currentSteps;
  }, [currentSteps]);

  // Handle incoming messages from ExecutionPanel
  useEffect(() => {
    if (incomingMessage) {
      setMessages((prev) => {
        const lastMsg = prev[prev.length - 1];

        // If the last message is from assistant, update it
        if (lastMsg && lastMsg.role === "assistant") {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMsg,
              //executionId: incomingMessage.executionId,
              content: incomingMessage.isPartial
                ? lastMsg.content + incomingMessage.content
                : incomingMessage.content, // If not partial (final), replace or append? Usually stream appends, final might be full text.
              // Logic: If stream, we append. If final, we might want to ensure it's correct.
              // But our ExecutionPanel logic sends chunks for stream, and full text for final?
              // Let's assume ExecutionPanel sends chunks for stream, and maybe full text for final.
              // If final response comes, we should probably just set it.
              // Wait, if we are streaming, we are building it up.
              // If we get a final response, it might be the whole thing or just the end.
              // Based on previous code: `updateLastAssistantMessage` appended.
              // Let's stick to appending if partial. If final, replace content if it's the full text, or append if it's a chunk.
              // The plan said: "If not isPartial (final), replace/finalize".
              // Let's assume final message contains the FULL content for safety, or we treat it as such.
            },
          ];
        }

        // If last message is user (or empty), create new assistant message
        return [
          ...prev,
          {
            id: Date.now().toString(),
            role: "assistant",
            content: incomingMessage.content,
            timestamp: new Date(),
          },
        ];
      });
    }
  }, [incomingMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // const updateLastAssistantMessage = (chunk: string, replace = false) => {
  //     setMessages(prev => {
  //         const lastMsg = prev[prev.length - 1];
  //         if (lastMsg && lastMsg.role === 'assistant') {
  //             return [
  //                 ...prev.slice(0, -1),
  //                 {
  //                     ...lastMsg,
  //                     content: replace ? chunk : lastMsg.content + chunk,
  //                     // Attach steps to the message so they stay with it in history (optional, currently using global steps)
  //                     // For a persistent chat, we'd want to snapshot steps here.
  //                 }
  //             ];
  //         } else {
  //             return [
  //                 ...prev,
  //                 {
  //                     id: Date.now().toString(),
  //                     role: 'assistant',
  //                     content: chunk,
  //                     timestamp: new Date()
  //                 }
  //             ];
  //         }
  //     });
  // };

  const handleSend = (inputValue) => {
    console.log("*** chat panel handleSend inputValue", inputValue);
    const desc = inputValue
      .replace(`@${agentName}`, "")
      .trim();
    if (!inputValue.trim()) return;
    const executionId = uuidv4();
    const userMsg = {
      id: Date.now().toString(),
      role: "user",
      content: desc,
      timestamp: new Date(),
      executionId: executionId,
    };

    setMessages((prev) => [...prev, userMsg]);
    onSendMessage(desc, executionId);

    // setInput('');

    // Prepare for assistant response immediately so the user sees "Thinking..."
    setMessages((prev) => [
      ...prev,
      {
        id: (Date.now() + 1).toString(),
        role: "assistant",
        content: "",
        timestamp: new Date(),
      },
    ]);
  };

  return (
    <div className="w-full h-full bg-gradient-to-br from-gray-50 via-white to-gray-50 flex flex-col">
      {/* Header */}
      {/* <div className="px-6 py-4 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 shadow-sm flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#1AA3A1] to-[#47B76E] rounded-xl flex items-center justify-center shadow-lg">
                        <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                        <h2 className="font-semibold text-gray-900">{agentConfig?.agent_config?.name}</h2>
                        <div className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 rounded-full bg-green-500`} />
                            <span className="text-xs text-gray-500">Have a conversation with your agent</span>
                        </div>
                    </div>
                </div>
            </div> */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-purple-50 via-pink-50 to-rose-50 flex-shrink-0 h-[60px]">
        <div>
          <h1 className="text-sm text-gray-900">
            Chat with {agentName}
          </h1>
          <p className="text-xs text-gray-500">
            Have a conversation with your agent
          </p>
        </div>
        {/* <Button
                    variant="ghost"
                    size="sm"
                    onClick={onToggleInfo}
                    className="gap-1.5 h-8 text-xs"
                >
                    <Info className="w-3.5 h-3.5" />
                    Info
                </Button> */}
      </div>
      <ScrollArea className="flex-1 px-6 py-8">
        <div className="max-w-3xl mx-auto space-y-6">
          {messages.length === 0 && !initialInput ? (
            <div className="flex flex-col items-center justify-center h-full py-24 text-center">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#1AA3A1]/10 to-[#47B76E]/10 flex items-center justify-center mb-6">
                <Brain className="w-10 h-10 text-[#1AA3A1]" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">
                Ready to Chat
              </h3>
            </div>
          ) : (
            messages.map((message, index) => (
              <div
                id={`execution-${message.executionId}`}
                key={message.id}
                className={`flex ${
                  message.role === "user" ? "justify-end" : "justify-start"
                } animate-in fade-in slide-in-from-bottom-2 duration-300`}
              >
                <div
                  className={`flex gap-3 max-w-[100%] ${
                    message.role === "user" ? "flex-row-reverse" : "flex-row"
                  }`}
                >
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center shadow-sm ${
                      message.role === "user"
                        ? "bg-gradient-to-br from-blue-500 to-purple-500"
                        : "bg-gradient-to-br from-[#1AA3A1] to-[#47B76E]"
                    }`}
                  >
                    {message.role === "user" ? (
                      <User className="w-4 h-4 text-white" />
                    ) : (
                      <Bot className="w-4 h-4 text-white" />
                    )}
                  </div>

                  <div className="flex flex-col gap-1 w-full">
                    {/* Progress Steps (Only for assistant and if it's the latest message or has steps attached) */}
                    {message.role === "assistant" &&
                      index === messages.length - 1 &&
                      currentSteps.length > 0 && (
                        <div className="mb-2 bg-white/50 rounded-xl border border-gray-100 overflow-hidden">
                          <button
                            onClick={() => setIsStepsExpanded(!isStepsExpanded)}
                            className="w-full flex items-center justify-between px-4 py-2 bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
                          >
                            <span className="text-xs font-medium text-gray-500 flex items-center gap-2">
                              <Loader2
                                className={`w-3 h-3 ${
                                  isProcessing ? "animate-spin" : ""
                                }`}
                              />
                              Agent Progress ({currentSteps.length})
                            </span>
                            {isStepsExpanded ? (
                              <ChevronUp className="w-3 h-3 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-3 h-3 text-gray-400" />
                            )}
                          </button>
                          {isStepsExpanded && (
                            <div className="px-4 py-2 space-y-1 bg-white/50">
                              {currentSteps.map((step) => (
                                <StepItem key={step.step} step={step} />
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                    <div
                      className={`rounded-2xl px-5 py-3.5 shadow-sm ${
                        message.role === "user"
                          ? "bg-gradient-to-br from-[#1AA3A1] to-[#1AA3A1]/90 text-white border-[#1AA3A1]/20"
                          : "bg-white border border-gray-100 text-gray-800"
                      }`}
                    >
                      {message.content ? (
                        <p className="text-sm leading-relaxed whitespace-pre-wrap">
                          {message.content}
                        </p>
                      ) : (
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <ChatInputLoader />
                          <span>Thinking...</span>
                        </div>
                      )}
                    </div>
                    <div
                      className={`flex items-center gap-1 px-1 ${
                        message.role === "user"
                          ? "justify-end"
                          : "justify-start"
                      }`}
                    >
                      <Clock className="w-3 h-3 text-gray-400" />
                      <span className="text-[10px] text-gray-400">
                        {message.timestamp.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      {/* Input Area */}
      {/* <div className="pt-4 pl-4 pr-4 border-t border-gray-200 bg-white flex-shrink-0 flex gap-2"> */}
      <ChatInput
        onSubmit={handleSend}
        agentId={agentId}
        agentName={agentName}
        placeholder="Type a message to start the conversation..."
        placement="top"
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        agentDetails={agentDetails}
        agentLists={agentLists}
        agentsLoading={agentsLoading}
      />
      {/* </div> */}
    </div>
  );
};

export default ChatPanel;
